import { useEffect, useState, type JSX } from 'react'
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  ToggleGroup,
  ToggleGroupItem
} from '@ttab/elephant-ui'
import { XIcon } from '@ttab/elephant-ui/icons'
import { useTranslation } from 'react-i18next'
import { Prompt } from '@/components/Prompt'
import { StructuredMode } from './StructuredMode'
import { QuerySyntaxMode } from './QuerySyntaxMode'
import { createDefaultState, createDefaultStructured, createQuerySyntaxState, isActiveState } from './lib/defaultState'
import { dropsDateRange, structuredToQueryString } from './lib/toQueryString'
import type { AdvancedSearchDialogProps, AdvancedSearchState, StructuredSearchState } from './types'

export const AdvancedSearchDialog = ({
  open,
  onOpenChange,
  fields,
  state,
  onApply,
  onClear
}: AdvancedSearchDialogProps): JSX.Element => {
  const { t } = useTranslation()
  const [draft, setDraft] = useState<AdvancedSearchState>(state)
  // What the other mode held before the switch, so toggling back and forth inside one
  // dialog does not destroy work. Neither is persisted - only the mode in use is applied.
  const [shelvedStructured, setShelvedStructured] = useState<StructuredSearchState>()
  const [shelvedRaw, setShelvedRaw] = useState<string>()
  // The generated string as handed over, to tell an untouched conversion from a hand edit.
  const [generatedRaw, setGeneratedRaw] = useState<string>()
  const [droppedDateRange, setDroppedDateRange] = useState(false)
  const [confirmingModeChange, setConfirmingModeChange] = useState(false)

  useEffect(() => {
    if (open) {
      setDraft(state)
      setShelvedStructured(undefined)
      setShelvedRaw(undefined)
      setGeneratedRaw(undefined)
      setDroppedDateRange(false)
      setConfirmingModeChange(false)
    }
  }, [open, state])

  const canApply = isActiveState(draft)
  // Emptying the mode whose search is applied means "drop it". Requiring both modes
  // to be empty left the button dead when the other one still held unapplied text,
  // and the mode check keeps merely switching to an empty tab from clearing.
  const canClear = !canApply && isActiveState(state) && draft.mode === state.mode

  function handleApply() {
    if (canApply) {
      onApply(draft)
      onOpenChange(false)
      return
    }

    if (canClear) {
      onClear()
      onOpenChange(false)
    }
  }

  function handleClear() {
    setDraft(draft.mode === 'querySyntax'
      ? createQuerySyntaxState('')
      : createDefaultState(fields))
    setShelvedStructured(undefined)
    setShelvedRaw(undefined)
    setGeneratedRaw(undefined)
    setDroppedDateRange(false)
    setConfirmingModeChange(false)
  }

  function toQuerySyntax() {
    if (draft.mode !== 'structured') {
      return
    }

    const converted = structuredToQueryString(draft.structured, fields)
    // Text the user wrote by hand outranks a fresh conversion of an empty form.
    const raw = converted || shelvedRaw || ''

    setShelvedStructured(draft.structured)
    setGeneratedRaw(converted ? raw : undefined)
    setDroppedDateRange(dropsDateRange(draft.structured))
    setDraft(createQuerySyntaxState(raw, draft.name))
  }

  function toStructured() {
    if (draft.mode !== 'querySyntax') {
      return
    }

    const raw = draft.querySyntax.raw
    // An untouched conversion still matches the form it came from, so that form can
    // come back. Anything else was written by hand and the form cannot express it -
    // a query string can differ per field where the form has one term for all of
    // them - so the switch stops and asks before the string is thrown away.
    const unchanged = generatedRaw !== undefined && generatedRaw === raw

    if (!unchanged && raw.trim().length > 0) {
      setConfirmingModeChange(true)
      return
    }

    setShelvedRaw(raw)
    setDroppedDateRange(false)
    setDraft({
      mode: 'structured',
      name: draft.name,
      structured: unchanged && shelvedStructured
        ? shelvedStructured
        : createDefaultStructured(fields)
    })
  }

  function discardQueryString() {
    setShelvedStructured(undefined)
    setShelvedRaw(undefined)
    setGeneratedRaw(undefined)
    setDroppedDateRange(false)
    setConfirmingModeChange(false)
    setDraft({ ...createDefaultState(fields), name: draft.name })
  }

  function handleModeChange(value: string) {
    if (value === draft.mode) {
      return
    }

    if (value === 'querySyntax') {
      toQuerySyntax()
    } else if (value === 'structured') {
      toStructured()
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange} modal>
        <DialogContent className='sm:max-w-md'>
          <DialogHeader className='flex flex-row items-center justify-between space-y-0'>
            <DialogTitle>{t('advancedSearch.title')}</DialogTitle>
            <DialogClose
              render={(
                <Button variant='ghost' className='w-8 h-8 p-0'>
                  <XIcon size={18} strokeWidth={1.75} />
                </Button>
              )}
            />
          </DialogHeader>

          <div className='flex flex-col gap-4'>
            <ToggleGroup
              type='single'
              value={[draft.mode]}
              onValueChange={(values: string[]) => handleModeChange(values[0])}
              className='justify-start'
            >
              <ToggleGroupItem value='structured' className='text-xs'>
                {t('advancedSearch.structured')}
              </ToggleGroupItem>
              <ToggleGroupItem value='querySyntax' className='text-xs'>
                {t('advancedSearch.querySyntax')}
              </ToggleGroupItem>
            </ToggleGroup>

            {droppedDateRange && (
              <p className='text-xs text-muted-foreground border-l-2 pl-2'>
                {t('advancedSearch.notices.dateRangeNotConverted')}
              </p>
            )}

            {draft.mode === 'structured'
              ? (
                  <StructuredMode
                    state={draft.structured}
                    fields={fields}
                    onChange={(structured) => setDraft({ ...draft, structured })}
                  />
                )
              : (
                  <QuerySyntaxMode
                    state={draft.querySyntax}
                    fields={fields}
                    onChange={(querySyntax) => setDraft({ ...draft, querySyntax })}
                  />
                )}

            <div className='pt-2 border-t'>
              <Input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder={t('advancedSearch.namePlaceholder')}
                className='text-sm'
              />
            </div>
          </div>

          <DialogFooter className='flex flex-row sm:justify-between justify-between gap-2 pt-4'>
            <Button variant='outline' onClick={handleClear}>
              {t('actions.clear')}
            </Button>
            <Button onClick={handleApply} disabled={!canApply && !canClear}>
              {t('advancedSearch.search')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {confirmingModeChange && (
        <Prompt
          title={t('advancedSearch.modeChange.title')}
          description={t('advancedSearch.modeChange.description')}
          primaryLabel={t('advancedSearch.modeChange.confirm')}
          onPrimary={discardQueryString}
          cancelLabel={t('actions.abort')}
          onCancel={() => setConfirmingModeChange(false)}
        />
      )}
    </>
  )
}
